import { useTranslation } from 'react-i18next';
import type { MeAccessTenant } from '@/api/meAccess';

export interface ProfileAccessTabProps {
    isLoading: boolean;
    softError: boolean;
    isSuccess: boolean;
    tenants: MeAccessTenant[];
    activeTenantId: string | null;
    onSwitchTenant: (tenantId: string) => void;
}

/** First letters of up to the first two words — "Trường Quốc tế EMG Hà Nội" → "TQ".
 *  Purely decorative (frame 78's org-avatar tile); never a stand-in for real data. */
function orgInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * `/profile` — "Quyền của tôi" tab (frame 78), read-only.
 *
 * EAA-1820: "Tổ chức bạn có quyền" reuses the org/role-chip render this page
 * already had (EAA-1822's access card) under the frame's own card chrome.
 *
 * TWO sections of frame 78 are NOT rendered, flagged here rather than faked:
 *   - "Vai trò console" (chips tagged "dựng sẵn" / "qua nhóm <tên>") — `/me/access`
 *     (`MeAccessTenant`) exposes a flat `roles: string[]` per org with no
 *     built-in-vs-group-derived provenance field, and no such distinction
 *     exists anywhere else the FE can read (checked `meAccess.ts`,
 *     `orgAdmin.ts`). Inventing the tag would misattribute where a role
 *     actually came from.
 *   - The "Đến từ" column of "Mã quyền bạn đang giữ" (which console role
 *     granted a given code) — same gap: `MeAccessTenant.permissions` is a flat
 *     `string[]`, not `{code, description, from_role}`. The catalog with
 *     human descriptions DOES exist (`fetchPermissionCatalog` /
 *     `/api/permissions/catalog`, used by `PermissionCatalogTab`), but it is
 *     gated on `console.permissions.view` — calling it from a plain
 *     non-admin caller's own `/profile` would 403 for exactly the persona
 *     frame 78 depicts (a teacher). So this renders the real codes only, as
 *     a plain list, with the gap stated in the card's own subtitle rather
 *     than a silently empty "Việc được làm"/"Đến từ" pair of columns.
 */
export function ProfileAccessTab({
    isLoading,
    softError,
    isSuccess,
    tenants,
    activeTenantId,
    onSwitchTenant,
}: ProfileAccessTabProps) {
    const { t } = useTranslation();
    const activeTenant = tenants.find((tn) => tn.tenant_id === activeTenantId);

    return (
        <>
            <div className="portal-profile-notice">
                <InfoIcon />
                <span>
                    {t('profile.accessTab.readonlyNoticePrefix')}
                    <strong>{t('profile.accessTab.readonlyNoticeBold')}</strong>
                    {t('profile.accessTab.readonlyNoticeSuffix')}
                </span>
            </div>

            <div className="portal-profile-card portal-profile-card-flush">
                <div className="portal-profile-info-card-header">
                    <span className="portal-profile-info-card-heading">
                        <span className="portal-profile-info-card-title">
                            {t('profile.accessTab.orgsCardTitle')}
                        </span>
                        <span className="portal-profile-info-card-desc">
                            {t('profile.accessTab.orgsCardDesc')}
                        </span>
                    </span>
                </div>

                {isLoading && <p aria-busy="true">{t('profile.accessCard.loading')}</p>}
                {softError && <p className="portal-profile-soft-error">{t('profile.accessCard.error')}</p>}
                {isSuccess && tenants.length === 0 && (
                    <p className="portal-profile-empty">{t('profile.accessCard.empty')}</p>
                )}

                {tenants.map((tenant) => {
                    const isActive = tenant.tenant_id === activeTenantId;
                    return (
                        <div key={tenant.tenant_id} className="portal-profile-org-row">
                            <span className="portal-profile-org-avatar" aria-hidden="true">
                                {orgInitials(tenant.name)}
                            </span>
                            <span className="portal-profile-org-row-info">
                                <span className="portal-profile-org-name">{tenant.name}</span>
                                <code className="portal-profile-org-code">{tenant.slug}</code>
                            </span>
                            <span className="portal-profile-org-roles">
                                {tenant.roles.length > 0 ? (
                                    tenant.roles.map((role) => (
                                        <span key={role} className="portal-pill">
                                            {role}
                                        </span>
                                    ))
                                ) : (
                                    <span className="portal-muted">{t('profile.noRole')}</span>
                                )}
                            </span>
                            {isActive ? (
                                <span className="portal-profile-active-tag">
                                    <CheckIcon />
                                    {t('profile.activeBadge')}
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    className="portal-profile-switch-link"
                                    onClick={() => onSwitchTenant(tenant.tenant_id)}
                                >
                                    {t('profile.switchTo')}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="portal-profile-card portal-profile-card-flush">
                <div className="portal-profile-info-card-header">
                    <span className="portal-profile-info-card-heading">
                        <span className="portal-profile-info-card-title">
                            {t('profile.accessTab.permsCardTitle')}
                        </span>
                        <span className="portal-profile-info-card-desc">
                            {activeTenant
                                ? t('profile.accessTab.permsCardDesc', {
                                      count: activeTenant.permissions.length,
                                  })
                                : t('profile.accessTab.noActiveOrgNotice')}
                        </span>
                    </span>
                </div>
                {activeTenant && activeTenant.permissions.length > 0 ? (
                    <div className="portal-profile-perms-table-wrap">
                        <table className="portal-profile-perms-table">
                            <thead>
                                <tr>
                                    <th>{t('profile.accessTab.permsCodeHeader')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeTenant.permissions.map((code) => (
                                    <tr key={code}>
                                        <td>
                                            <code>{code}</code>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    activeTenant && <p className="portal-profile-empty">{t('profile.accessTab.permsEmpty')}</p>
                )}
            </div>
        </>
    );
}

function InfoIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
            className="portal-profile-notice-icon"
        >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5M12 8h.01" strokeLinecap="round" />
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
            <path d="m5 12.5 4.5 4.5L19 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export default ProfileAccessTab;
